'use client';

import Image from 'next/image';
import { FooterButton } from '@/components/ui/common/buttons';
import clsx from 'clsx';
import useCookieContext from '@/components/providers/context/cookie-context';
import { useTranslation } from 'react-i18next';
import CustomLink from '@/components/ui/common/custom-link';
import { JContainer } from '../../layout';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

export default function CookieOptions({ locale }: { locale: string }) {
    const {
        getAcceptStatus,
        showCookieSettings,
        hideAccept,
        createUserConsent,
        updateCookiesRange,
    } = useCookieContext();

    const { t } = useTranslation('cookie');
    const handleAcceptAll = () => {
        createUserConsent('agree');
        updateCookiesRange('all');
        setTimeout(() => {
            hideAccept();
        }, 300);
    };

    const handleRejectAll = () => {
        createUserConsent('reject');
        updateCookiesRange('none');
        setTimeout(() => {
            hideAccept();
        }, 300);
    };

    const acceptStatus = getAcceptStatus();

    return (
        <section
            className={clsx(
                'fixed bottom-0 left-0 z-[9998] flex w-full items-center justify-center px-[16px] pb-[25px] sm:px-[40px]',
                {
                    'invisible scale-90 opacity-0': !acceptStatus,
                    'visible scale-100 opacity-100': acceptStatus,
                },
            )}
        >
            <JContainer
                size="lg2"
                padding="none"
                className="tablet:gap-[25px] tablet:rounded-[16px] tablet:p-[24px] flex h-full w-full flex-col gap-0 rounded-[8px] bg-[#1b1b24b3] p-[16px] backdrop-blur-[24px] backdrop-filter lg:flex-row lg:gap-0"
            >
                <div className="tablet:flex-row flex flex-col items-center justify-center">
                    <div className="flex flex-none items-center justify-center pr-0 sm:pr-[16px]">
                        <Image
                            src={getStaticAssetUrl('/icon-cookie.webp')}
                            width={40}
                            height={40}
                            alt="icon-cookie"
                        />
                    </div>
                    <div
                        className={clsx(
                            'j-t3 mr-0 mt-[16px] grow text-white sm:mr-[40px] sm:mt-0',
                        )}
                    >
                        {t('text.part1')}
                        <CustomLink
                            href={'/' + locale + '/cookie'}
                            fill="#FFFFFF"
                        >
                            {t('text.part2')}
                        </CustomLink>
                        {t('text.part3')}
                    </div>
                </div>

                <div className="mt-[24px] flex flex-none flex-col items-center justify-center gap-[16px] sm:mt-0 sm:flex-row sm:items-center sm:justify-start">
                    <div className="flex w-full flex-row items-center justify-between gap-[16px] sm:w-auto">
                        <FooterButton
                            icon={<></>}
                            iconSize={[0, 0]}
                            iconSize4Mobile={[0, 0]}
                            className={clsx(
                                'j-t2 h-[36px] w-full sm:h-[40px] sm:w-auto',
                            )}
                            text={t('button.setting')}
                            textColor="#FFFFFF"
                            textHoverColor="#000000"
                            borderColor="#FFFFFF"
                            boderHoverColor="#FFFFFF"
                            bgHoverColor="#FFFFFF"
                            onClick={showCookieSettings}
                            withIcon={false}
                            keepCenter={true}
                        />
                        <FooterButton
                            icon={<></>}
                            iconSize={[0, 0]}
                            iconSize4Mobile={[0, 0]}
                            className={clsx(
                                'j-t2 h-[36px] w-full sm:h-[40px] sm:w-auto',
                            )}
                            textColor="#FFFFFF"
                            textHoverColor="#000000"
                            borderColor="#FFFFFF"
                            boderHoverColor="#FFFFFF"
                            bgHoverColor="#FFFFFF"
                            onClick={handleRejectAll}
                            text={t('button.reject')}
                            withIcon={false}
                            keepCenter={true}
                        />
                    </div>

                    <FooterButton
                        className={clsx(
                            'j-t2 h-[36px] w-full sm:h-[40px] sm:w-auto lg:w-full',
                        )}
                        iconSize={[15, 15]}
                        iconSize4Mobile={[15, 15]}
                        textColor="#FFFFFF"
                        textHoverColor="#FFFFFF"
                        borderColor="#6038FF"
                        boderHoverColor="#7B5BFF"
                        bgHoverColor="#7B5BFF"
                        bgColor="#6038FF"
                        onClick={handleAcceptAll}
                        text={t('button.accept')}
                        iconColor="#FFFFFF"
                        iconHoverColor="#FFFFFF"
                        withIcon={true}
                        keepCenter={true}
                    />
                </div>
            </JContainer>
        </section>
    );
}
